import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { useModalStore } from '../../store/useModalStore'
import { usePopover } from '../../store/usePopoverStore'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  useQuotationDefault,
  useQuotationRecent,
} from '../../lib/apis/hooks/quotation.hook'
import {
  PARTNER_QUERY_KEY,
  usePartnerDetail,
  usePartnerQuotation,
} from '../../lib/apis/hooks/partner.hook'
import { postPartnerQuotation } from '../../lib/apis/v1/partner.service'
import { patchStoreBan, patchStoreBanRevert } from '../../lib/apis/v1/store.service'
import * as React from 'react'
import {
  newInitQuotationForm,
  type additionalQuotationObject,
  type dpsActiveObject,
  type dpsQuotationObject,
  type newKidsQuotationObject,
  type newLowsQuotationObject,
  type newNormalQuotationObject,
  type newQuotationForm,
  type normalQuotationObject,
  type PERIOD_TYPE,
  type usimQuotationObject,
} from '../../lib/apis/dtos/request/quotation.request'
import {
  postDpActive,
  postQuotation,
} from '../../lib/apis/v1/quotation.service'
import { getQueryClient } from '../../config/queryClient.config'
import ConfirmPopover from '../../components/ui/popover/confirm.component'
import { Checkbox, MenuItem, Popover, Select, Switch } from '@mui/material'
import FailSnackbar from '../../components/ui/snackbar/fail.component'
import SuccessSnackbar from '../../components/ui/snackbar/success.component'
import DeletePopover from '../../components/ui/popover/delete.component'
import ExplainTitle from '../../components/layout/header/explain-title'
import { formatDate, formatDateTime } from '../../utils/date.utils'
import Button from '../../components/ui/button'
import { cn } from '../../utils/cn.utils'
import useIsNightOrEarly from '../../lib/hooks/useIsNighatOrEarly'
import {
  useSensors,
  useSensor,
  PointerSensor,
  DndContext,
  closestCenter,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { arrayMove } from '@dnd-kit/sortable'
import { defaultPopoverInit } from '../../lib/constants/popover.constants'
import { MODAL_KEY } from '../../lib/constants/modal.constants'
import { PriceInput } from '../../components/ui/input/price.component'
import { PeriodSelect } from '../../components/ui/select/period.component'
import DraggableRow from '../../components/ui/table/draggable-row.component'
import type { RecentQuotationItem } from '../../lib/apis/dtos/response/quotation.response'
import { useNavigationGuard } from '../../lib/hooks/useNavigationGuard'
import ActiveBranchHeader from '../../components/layout/header/active-branch'
import { useAuth } from '../../store/useAuthStore'
import { ROLES } from '../../types/auth.types'
import {
  focusQuotationSubmitServerError,
  submitCondition,
  transformToNewForm,
} from '../../utils/quotation.utils'
import { toast } from 'sonner'
import CheckCircleSVG from '../../assets/svg/check-circle.svg?react'
import Error403 from '../../components/layout/errors/403'
import BundleSaleTable from '../../components/quotation/bundle-sale-table.component'
import {
  initBundleSales,
  withBundleSalePayload,
} from '../../lib/apis/dtos/bundle-sale.dto'

const DEFAULT_TH =
  'bg-[#1D1B20] text-[#ffffff] border border-[#DED8E1] font-[500] text-[11px] font-medium'
const DEFAULT_TD =
  'border border-[#DED8E1] bg-[#fff] p-[12px] text-[11px] font-medium'
const COLOR_TD = 'border border-[#DED8E1] px-[8px] py-[12px]'
const SK_TH = 'bg-[#FB5353] text-[#ffffff] border border-[#DED8E1]'
const KT_TH = 'bg-[#369EFF] text-[#ffffff] border border-[#DED8E1]'
const LG_TH = 'bg-[#8539F7] text-[#ffffff] border border-[#DED8E1]'

interface CoverProps {
  title: string
  description: string
  children?: React.ReactNode
  titleChildren?: React.ReactNode
}

const LOW_HEADER = [
  { label: '순서', width: '', require: false },
  { label: '휴대폰', width: '', require: false },
  { label: '용량', width: '', require: false },
  { label: '통신사', width: '', require: true },
  { label: '구분', width: '', require: false },
  { label: '필수 요금제 (원)', width: '', require: true },
  { label: '필수 유지기간', width: '260', colspan: 2, require: true },
  { label: '번이(만원)', width: '', require: false },
  { label: '기변(만원)', width: '', require: false },
  { label: '삭제', width: '', require: false },
]

const DP_HEADER = [
  { label: '순서', width: '', require: false },
  { label: '휴대폰', width: '', require: false },
  { label: '용량', width: '', require: false },
  { label: '색상', width: '', require: true },
  { label: '통신사', width: '', require: true },
  { label: '필수 요금제 (원)', width: '', require: true },
  { label: '필수 유지기간', width: '', colspan: 2, require: true },
  { label: '번이(만원)', width: '', require: false },
  { label: '기변(만원)', width: '', require: false },
  { label: '판매중', width: '', require: false },
  { label: '삭제', width: '', require: false },
]

const KIDS_HEADER = [
  { label: '순서', width: '', require: false },
  { label: '휴대폰', width: '', require: false },
  { label: '용량', width: '', require: false },
  { label: '통신사', width: '', require: true },
  { label: '구분', width: '', require: false },
  { label: '필수 요금제 (원)', width: '', require: true },
  { label: '필수 유지기간', width: '260', colspan: 2, require: true },
  { label: '신규(만원)', width: '', require: false },
  { label: '번이(만원)', width: '', require: false },
  { label: '기변(만원)', width: '', require: false },
  { label: '삭제', width: '', require: false },
]

const ADDITIONAL_HEADER = [
  { label: '통신사', width: '', require: false },
  { label: '부가서비스명', width: '', require: false },
  { label: '유지기간 단위', width: '', require: true },
  { label: '유지기간', width: '', require: true },
  { label: '기기에 따라 요금\n상이', width: '', require: false },
  { label: '월 요금(원)', width: '', require: true },
  { label: '필수 여부', width: '', require: false },
  { label: '미가입시 추가금\n(만원)', width: '', require: false },
  { label: '삭제', width: '', require: false },
]

const USIM_HEADER = [
  { label: '순서', width: '', require: false },
  { label: '통신사', width: '', require: true },
  { label: '(선약 할인 전)필수 요금제 (원)', width: '', require: true },
  { label: '필수 유지기간', width: '', colspan: 2, require: true },
  { label: '현금 페이백 금액(만원)', width: '', require: true },
  { label: '삭제', width: '', require: false },
]

const TELECOM_TYPE = {
  SK: 'SKT',
  KT: 'KT',
  LG: 'LG U+',
}

const QUOTATION_MENU = [
  { label: '필수 요금/ 유지 기간', value: 'fee' },
  { label: '일괄 정책 시세', value: 'main' },
  { label: '동시판매 추가 할인', value: 'bundle' },
  { label: '별도 정책 시세', value: 'lows' },
  { label: '키즈폰 시세', value: 'kids' },
  { label: '부가서비스', value: 'additional' },
  { label: '이벤트', value: 'event' },
  { label: '', value: 'divider' },
  { label: '유심이동', value: 'usim' },
]

const CoverWrapper = (props: CoverProps) => {
  return (
    <div className="bg-[#F0F3F4]">
      <div className="flex-row-between">
        <h1 className="px-[12px] py-[14px] text-[16px] font-bold whitespace-nowrap">
          {props.title}
        </h1>
        {props.titleChildren}
      </div>
      <p className="px-[12px] py-[6px] text-[14px] whitespace-pre-line text-[#B3261E]">
        {props.description}
      </p>

      {props.children}
    </div>
  )
}

interface QuotationDetailPageProps {
  /**
   * store: 지점 시세표 (/quotation/:id, /v1/quotation)
   * partner: 제휴 업체 시세표 (/partner/quotation/:id, /v1/partner/quotation) — 같은 화면을 제휴용으로 렌더링
   */
  mode?: 'store' | 'partner'
}

export default function QuotationDetailPage({ mode = 'store' }: QuotationDetailPageProps) {
  const isPartner = mode === 'partner'
  // 제휴 시세표는 일반(주요폰)·키즈 정책만 — 별도 정책(lows)·유심·이벤트 제외
  const HIDDEN_SECTIONS_FOR_PARTNER = ['lows', 'usim', 'event', 'divider']
  const isSectionHidden = (value: string) =>
    isPartner && HIDDEN_SECTIONS_FOR_PARTNER.includes(value)
  const auth = useAuth()
  const navigate = useNavigate()
  const { id } = useParams()
  const parsedId = typeof id === 'string' ? id : Array.isArray(id) ? id[0] : '0'

  const { openModal } = useModalStore()
  const pushPopover = usePopover((state) => state.pushPopover)
  const popPopover = usePopover((state) => state.popPopover)

  const isNightOrEalry = useIsNightOrEarly()

  const storeQuery = useQuery({
    ...useQuotationRecent(parsedId),
    enabled: !!id && !isPartner,
  })
  const partnerQuotationQuery = useQuery({
    ...usePartnerQuotation(parsedId),
    enabled: !!id && isPartner,
  })
  const { data: partnerDetailData } = useQuery({
    ...usePartnerDetail(parsedId),
    enabled: !!id && isPartner,
  })
  const partnerDetail = partnerDetailData?.result

  const { data, refetch, isSuccess } = isPartner ? partnerQuotationQuery : storeQuery

  const { refetch: initRefetch } = useQuery({
    ...useQuotationDefault(parsedId),
    enabled: false,
  })

  const { result: rawResult, code } = data || {}
  // 제휴 시세표 응답에는 업체명/기간이 없어 제휴 상세로 채운다 (activeTo → endDate)
  // result 는 아래 useEffect 의존성이므로 참조가 매 렌더 바뀌지 않게 useMemo 로 고정
  const result = React.useMemo(
    () =>
      isPartner && rawResult
        ? {
            ...rawResult,
            storeName: partnerDetail?.partnerName ?? '',
            isActive: partnerDetail?.isActive ?? rawResult.isActive,
            startDate: partnerDetail?.activeFrom ?? rawResult.startDate ?? '',
            endDate: partnerDetail?.activeTo ?? rawResult.endDate ?? '',
          }
        : rawResult,
    [isPartner, rawResult, partnerDetail],
  )

  const isAuthError = code === 403 || code === 10002;

  const [form, setForm] = React.useState<newQuotationForm>(newInitQuotationForm)
  const [activeId, setActiveId] = React.useState('fee-section')

  const sensors = useSensors(useSensor(PointerSensor))

  const postMutation = useMutation({
    mutationFn: (form: newQuotationForm) => {
      // 동시판매(bundleSales)는 서버 필드명으로 변환해 같이 보낸다 (bundle-sale.dto.ts)
      const payload = withBundleSalePayload(form) as unknown as newQuotationForm
      return isPartner
        ? postPartnerQuotation({ partnerId: parsedId, form: payload })
        : postQuotation({ ...payload, storeId: parseInt(parsedId) })
    },
    onSuccess: (data, submittedForm) => {
      getQueryClient().invalidateQueries({
        queryKey: ['quotation-list', 'quotation-recent'],
      })
      if (isPartner) {
        getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.list] })
        getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.detail, parsedId] })
        getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.quotation, parsedId] })
      }

      const { error, code, message } = data || {}
      if (error) {
        focusQuotationSubmitServerError(code, submittedForm)
        FailSnackbar(message || '시세표 제출 실패')
      } else {
        SuccessSnackbar('시세표 제출 완료')
      }
      refetch()
    },
    onError: () => {
      FailSnackbar('시세표 제출 실패')
    },
  })

  // 지점 정지 상태 (제휴 업체는 정지 개념 없음)
  const isBanned = !isPartner && !!result?.isBanned
  const banTo = result?.banTo ?? null
  const canSubmit = !!result?.isActive && !isBanned

  // 온라인 지점은 당일 개통 마감(20시~익일 10시)과 무관하게 언제든 시세표를 제출할 수 있다
  const isOnlineStore = !!result?.isOnline
  const isSubmitTimeBlocked =
    auth.role !== ROLES.ADMIN && isNightOrEalry && !isOnlineStore

  const banMutation = useMutation({
    mutationFn: patchStoreBan,
    onSuccess: (res) => {
      if (res.error) {
        FailSnackbar(res.message || '지점 정지 실패')
        return
      }
      SuccessSnackbar('지점 정지 처리 완료')
      getQueryClient().invalidateQueries({ queryKey: ['quotation-list'] })
      getQueryClient().invalidateQueries({ queryKey: ['store-list'] })
      getQueryClient().invalidateQueries({ queryKey: ['store-detail'] })
      getQueryClient().invalidateQueries({ queryKey: ['store-date-log'] })
      refetch()
    },
    onError: () => FailSnackbar('지점 정지 실패'),
  })

  const openBanModal = () =>
    openModal(MODAL_KEY.BranchSuspend, {
      onConfirm: (day) => banMutation.mutate({ id: Number(parsedId), day }),
    })

  const banRevertMutation = useMutation({
    mutationFn: patchStoreBanRevert,
    onSuccess: (res) => {
      if (res.error) {
        FailSnackbar(res.message || '지점 정지 해제 실패')
        return
      }
      SuccessSnackbar('지점 정지 해제 완료')
      getQueryClient().invalidateQueries({ queryKey: ['quotation-list'] })
      getQueryClient().invalidateQueries({ queryKey: ['store-list'] })
      getQueryClient().invalidateQueries({ queryKey: ['store-detail'] })
      getQueryClient().invalidateQueries({ queryKey: ['store-date-log'] })
      refetch()
    },
    onError: () => FailSnackbar('지점 정지 해제 실패'),
  })

  const banRevertPopoverClick = (event: React.MouseEvent<HTMLElement>) =>
    pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
        PaperProps: { sx: { mt: 1 } },
      },
      (popoverId) => (
        <ConfirmPopover
          id={popoverId}
          rightProps={{
            label: '지점 정지 해제',
            variant: 'primary',
            onClick: () => {
              popPopover(popoverId)
              banRevertMutation.mutate(parsedId)
            },
          }}
        >
          <p className="text-[14px]">
            지점 정지를 해제하시겠습니까? 해당 지점이 활성화되고 시세표를 작성할
            수 있게됩니다.
          </p>
        </ConfirmPopover>
      ),
    )

  const postDpActiveMutation = useMutation({
    mutationFn: (form: dpsActiveObject) => postDpActive(form),
    onSuccess: () => {
      refetch()
    },
    onError: () => {
      FailSnackbar('DP 활성화 실패')
    },
  })

  const postPopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    return pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
        PaperProps: {
          sx: { mt: 3 },
        },
      },
      (id) => (
        <ConfirmPopover
          id={id}
          rightProps={{
            label: '제출',
            variant: 'primary',
            onClick: async () => {
              popPopover(id)
              const isReadyToSubmit = submitCondition(form)
              if (!isReadyToSubmit) return

              // dps 순서 정렬
              let _form = { ...form }
              if (_form.dps && _form.dps.length > 0) {
                _form.dps = _form.dps.map((item, index) => ({
                  ...item,
                  sortOrder: index + 1,
                }))
              }

              postMutation.mutate(_form)
            },
          }}
        >
          <p>
            시세표를 제출하시겠습니까? 입력
            <br />
            된 시세표대로 고객에게 견적이 나<br />
            갑니다.
          </p>
        </ConfirmPopover>
      ),
    )
  }

  const initPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    return pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
        PaperProps: {
          sx: { mt: 3 },
        },
      },
      (id) => (
        <ConfirmPopover
          id={id}
          rightProps={{
            label: '불러오기',
            variant: 'primary',
            onClick: async () => {
              popPopover(id)
              initRefetch().then((res) => {
                const { result: initResult } = res.data || {}
                const _form = transformToNewForm(
                  initResult as RecentQuotationItem | newQuotationForm,
                )
                setForm(_form)
              })
            },
          }}
        >
          <p>
            모두의 성지에서 제공하는 기본 시세표를 불러오시겠습니까? 현재 입력한
            값들이 있다면 모두 사라집니다.
          </p>
        </ConfirmPopover>
      ),
    )
  }

  const deletePopoverClick = (
    event: React.MouseEvent<HTMLDivElement>,
    onClick: () => void,
  ) => {
    return pushPopover(
      Popover,
      event.currentTarget,
      defaultPopoverInit,
      (id) => (
        <DeletePopover
          id={id}
          title={'삭제하시겠습니까? 입력한 값이 모두 사라집니다.'}
          rightButton={{
            onClick: onClick,
          }}
        />
      ),
    )
  }

  const handleTdClick = (e: React.MouseEvent<HTMLTableCellElement>) => {
    const targetInput = e.currentTarget.querySelector(
      'input',
    ) as HTMLInputElement
    targetInput?.focus()
  }

  const handleConfirm = React.useCallback(() => {
    return new Promise<boolean>((resolve) => {
      const modalProps = {
        leftButton: { onClick: () => { resolve(false); } },
        rightButton: { onClick: () => { resolve(true); } },
        onClose: () => { resolve(false); }
      };
      openModal(MODAL_KEY.NaviConfirm, modalProps as any);
    });
  }, [openModal]);

  useNavigationGuard({
    enabled: !isAuthError,
    confirm: handleConfirm,
  })

  // HOOKS
  React.useEffect(() => {
    const mapForm = transformToNewForm(
      result as RecentQuotationItem | newQuotationForm,
    )

    const lowsWithUUID = (mapForm?.lows || []).map((item) => ({
      ...item,
      uuid: uuidv4(),
    }))

    const kidsWithUUID = (mapForm?.kids || []).map((item) => ({
      ...item,
      uuid: uuidv4(),
    }))

    const dpsWithUUID = (mapForm?.dps || []).map((item) => ({
      ...item,
      uuid: uuidv4(),
    }))

    setForm({
      ...mapForm,
      lows: lowsWithUUID,
      kids: kidsWithUUID,
      dps: dpsWithUUID,
    })
  }, [isSuccess, result])

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting)
        if (visible) setActiveId(visible.target.id)
      },
      {
        threshold: 0.8,
      },
    )

    QUOTATION_MENU.forEach(({ value }) => {
      const el = document.getElementById(`${value}-section`)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  const getSelectedTelecomsByDevice = (
    deviceId: number,
    value: 'kids' | 'lows' | 'dps',
    storage?: string,
    color?: string,
  ) => {
    return (form[value] || [])
      .filter((item) => {
        if (value === 'lows') {
          return item.deviceId === deviceId && item.storage === storage
        } else if (value === 'dps') {
          return (
            item.deviceId === deviceId &&
            item.storage === storage &&
            (item as dpsQuotationObject).color === color
          )
        } else {
          return item.deviceId === deviceId
        }
      })
      .map((item) => item.telecom)
  }

  const rowsWithStableIds = React.useMemo(() => {
    return (form?.lows || []).map((item) => ({
      ...item,
      // item에 uuid가 없으면 새로 생성하고, 있으면 기존 값을 사용합니다.
      uuid: item.uuid || uuidv4(),
    }))
  }, [form?.lows])

  const rowsWithStableIdsDps = React.useMemo(() => {
    return (form?.dps || []).map((item) => ({
      ...item,
      // item에 uuid가 없으면 새로 생성하고, 있으면 기존 값을 사용합니다.
      uuid: item.uuid || uuidv4(),
    }))
  }, [form?.dps])

  const updateMainItem = (index: number, field: string, value: any) => {
    setForm((prev) => ({
      ...prev,
      normals: (prev.normals || []).map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    }))
  }

  const updateLowsItem = (index: number, field: string, value: any) => {
    const updateState = (index: number, field: string, value: any) => {
      setForm((prev) => ({
        ...prev,
        lows: (prev.lows || []).map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item,
        ),
      }))
    }

    const isNumbericField = ['fee', 'period'].includes(field)

    if (isNumbericField) {
      if (value === '') {
        updateState(index, field, '')
        return
      }
      const numbericValue = Number(value)

      if (isNaN(numbericValue) || numbericValue <= 0) {
        return
      }
      updateState(index, field, numbericValue)
    } else {
      updateState(index, field, value)
    }
  }

  const updateDpsItem = (index: number, field: string, value: any) => {
    const updateState = (index: number, field: string, value: any) => {
      setForm((prev) => ({
        ...prev,
        dps: (prev.dps || []).map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item,
        ),
      }))
    }

    const isNumbericField = ['fee', 'usePeriod'].includes(field)
    if (isNumbericField) {
      if (value === '') {
        updateState(index, field, '')
        return
      }

      const numbericValue = Number(value)

      if (isNaN(numbericValue) || numbericValue <= 0) {
        return
      }
      updateState(index, field, numbericValue)
    } else {
      updateState(index, field, value)
    }
  }

  const updateKidsItem = (index: number, field: string, value: any) => {
    const updateState = (index: number, field: string, value: any) => {
      setForm((prev) => ({
        ...prev,
        kids: (prev.kids || []).map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item,
        ),
      }))
    }

    const isNumbericField = ['fee', 'period'].includes(field)

    if (isNumbericField) {
      if (value === '') {
        updateState(index, field, '')
        return
      }

      const numbericValue = Number(value)

      if (isNaN(numbericValue) || numbericValue <= 0) {
        return
      }
      updateState(index, field, numbericValue)
    } else {
      updateState(index, field, value)
    }
  }

  const updateUsimItem = (index: number, field: string, value: any) => {
    const updateState = (index: number, field: string, value: any) => {
      setForm((prev) => ({
        ...prev,
        usims: (prev.usims || []).map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item,
        ),
      }))
    }

    const isNumbericField = ['fee', 'usePeriod'].includes(field)

    if (isNumbericField) {
      if (value === '') {
        updateState(index, field, '')
        return
      }

      const numbericValue = Number(value)

      if (isNaN(numbericValue) || numbericValue <= 0) {
        return
      }
      updateState(index, field, numbericValue)
    } else {
      updateState(index, field, value)
    }
  }

  const updateAdditionalItem = (index: number, field: string, value: any) => {
    const updateState = (val: any) => {
      setForm((prev) => ({
        ...prev,
        additionalServices: (prev.additionalServices || []).map((item, idx) =>
          idx === index ? { ...item, [field]: val } : item,
        ),
      }))
    }

    const numericFields = ['duration', 'serviceFee', 'nonSubscriptionFee']

    if (!numericFields.includes(field)) {
      updateState(value)
      return
    }

    if (value === '') {
      updateState('')
      return
    }

    const numericValue = Number(value)

    if (isNaN(numericValue)) return
    const minValue = field === 'serviceFee' ? 0 : 1

    if (numericValue < minValue) {
      return
    }

    updateState(numericValue)
  }


  React.useEffect(() => {
    if (isAuthError || isPartner) return;

    if (isSuccess && !result?.isActive) {
      openModal(MODAL_KEY.Alert, {
        title: '월 결제하지 않은 비활성 지점이에요',
        content:
          '시세표 내용을 제출하고 고객에게 보여지게하기 위해선 지점을 활성화 시켜야해요.\n{https://pf.kakao.com/_xdxhxlan}로 문의주세요.',
      })
    }
  }, [isSuccess, result?.isActive, isAuthError, isPartner])

  if (isAuthError) {
    return <Error403 />;
  }


  return (
    <React.Fragment>
      {isBanned ? (
        <ActiveBranchHeader banned banTo={banTo} />
      ) : (
        result?.isActive && <ActiveBranchHeader endDate={result.endDate} />
      )}
      <div className="p-6">
        <ExplainTitle
          backOnclick={() => navigate(isPartner ? '/partner' : '/quotation')}
          title={`[${result?.storeName || '-'}] ${formatDate(
            new Date(),
          )} 가격 입력`}
          explanation={(
            <div className='flex-row gap-4 mt-1'>
              {result?.isOnline ? (
                <p className='text-style-body-03 font-normal'>온라인</p>
              ) : (
                <div className='flex-row gap-2'>
                  <div className="flex-row gap-1">
                    <p className="text-style-compact-02 font-semibold px-2 py-1 rounded-1 bg-green-05 text-green-60">주</p>
                    <p className="text-style-body-03 font-normal">{result?.mainZone?.region || ""} {result?.mainZone?.zoneName || ""}</p>
                  </div>
                  {(result?.subZones || []).length > 0 && (
                    <>
                      <div className="flex-row gap-1">
                        <p className="text-style-compact-02 font-semibold px-2 py-1 rounded-1 bg-gray-05 text-gray-60">인근</p>
                        <p className="text-style-body-03 font-normal">{result?.subZones.map((zone) => `${zone.region} ${zone.zoneName}`).join(', ')}</p>
                      </div>
                    </>
                  )}
                </div>

              )}
              <div className='w-px h-3 bg-black/12' />
              <p>{`${formatDate(
                new Date(),
              )} 20:00(당일 개통 마감)까지 유효한 가격입니다.`}</p>
            </div>
          )}
          titleSuffix={
            <div className="flex-row gap-3 pl-2">
              <Button
                variant={result?.isActive && !isBanned ? 'primary' : 'gray'}
                label={result?.isActive && !isBanned ? '활성' : '비활성'}
                className="h-8 rounded-[100px] px-[10px] text-[13px] font-normal"
              />
              {!isPartner && auth.role === ROLES.ADMIN && (
                <React.Fragment>
                  {isBanned ? (
                    <span
                      className="text-primary-50 cursor-pointer text-[14px] font-medium whitespace-nowrap hover:underline"
                      onClick={banRevertPopoverClick}
                    >
                      지점 정지 해제
                    </span>
                  ) : (
                    result?.isActive && (
                      <span
                        className="text-red-50 cursor-pointer text-[14px] font-medium whitespace-nowrap hover:underline"
                        onClick={openBanModal}
                      >
                        지점 정지처리
                      </span>
                    )
                  )}
                </React.Fragment>
              )}
            </div>
          }
        />

        <div className="flex-row-between">
          {isPartner ? (
            auth.role === ROLES.ADMIN ? (
              <div className="flex-row gap-[12px]">
                <Button
                  variant={'black'}
                  label="시세표 가져오기"
                  onClick={() =>
                    openModal(MODAL_KEY.QuotationImport, {
                      currentForm: form,
                      onImport: setForm,
                      defaultStore: partnerDetail?.referenceStore
                        ? { id: partnerDetail.referenceStore.id, storeName: partnerDetail.referenceStore.name }
                        : null,
                    })
                  }
                />
                <span className="text-[14px] text-black/60">
                  *다른 업체의 최근 시세표를 불러옵니다. (제출 아님)
                  <br />
                  현재 입력한 값들이 있다면 모두 사라집니다.
                </span>
              </div>
            ) : (
              <div />
            )
          ) : (
          <div className="flex-row gap-[12px]">
            <Button
              variant={'outline'}
              label="기본 시세표 불러오기"
              onClick={initPopover}
            />
            <span className="text-black/60">
              *모두의 성지에서 제공하는 초기 기본 설정된 시세표를 불러옵니다.
              <br />
              현재 입력한 값들이 있다면 모두 사라집니다.{' '}
            </span>
          </div>
          )}
          <div className="flex-row gap-3">
            {isSubmitTimeBlocked ? (
              <span className="text-right text-[14px] text-[#007AFF]">
                *당일 20시~익일 10시엔 당일 개통이 마감되어
                <br />
                시세표 수정이 제한됩니다. (익일 10시 이후 오픈 예정)
              </span>
            ) : (
              <React.Fragment>
                <span
                  className={cn(
                    'text-right text-[14px] whitespace-pre-line',
                    isBanned ? 'text-red-50' : 'text-[#007AFF]',
                  )}
                >
                  {isBanned
                    ? `*시세표 제출이 정지되었습니다.\n(~${banTo || '-'})`
                    : result?.isActive
                      ? `*시세표 제출 버튼을 눌러야 가격이 적용됩니다. \n (최근 제출 :${form?.createAt ? formatDateTime(form?.createAt) : '-'})`
                      : isPartner
                        ? `*비활성 제휴 업체입니다. 제휴 업체 수정에서 활성화 후 제출해주세요.`
                        : `*비활성 지점의 경우, 월 결제가 필요합니다.\n{https://pf.kakao.com/_xdxhxlan}로 문의주세요.`}
                </span>
                <Button
                  disabled={!canSubmit}
                  variant={canSubmit ? 'primary' : 'gray'}
                  label="시세표 제출"
                  onClick={postPopoverClick}
                />
              </React.Fragment>
            )}
          </div>
        </div>

        <div className="mt-[24px] flex-row items-start gap-[12px]">
          <div className="sticky top-[-5px] h-fit">
            {QUOTATION_MENU.filter(
              ({ value }) => !isSectionHidden(value),
            ).map(({ label, value }, index) => {
              if (value === 'divider') {
                return (
                  <div key={index} className="my-2 h-px w-full bg-black/10" />
                )
              }
              return (
                <div
                  key={index}
                  className={cn(
                    'flex h-10 min-w-[160px] cursor-pointer items-center px-4 py-1 text-[14px]',
                    activeId === `${value}-section`
                      ? 'bg-[#1976D214]'
                      : 'bg-[#ffffff]',
                  )}
                  onClick={() => {
                    const _value = `${value}-section`
                    setActiveId(_value)
                    document
                      .getElementById(_value)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  <span className="py-1">{label}</span>
                </div>
              )
            })}
          </div>
          <div className="flex w-full flex-col gap-y-[24px] border-l border-[#1D1B20]/16 pl-4">
            <section key={'fee-section'} id={`fee-section`}>
              <CoverWrapper
                title="일괄 정책 가격입력"
                description={`*단위 없이 숫자만 입력해주세요.`}
                titleChildren={
                  <div className="w-full flex-row justify-baseline">
                    <p
                      className={cn(
                        'text-[14px] font-semibold',
                        form.isMainFedActive
                          ? 'text-primary-50'
                          : 'text-gray-40',
                      )}
                    >
                      선택약정 시세표 {form.isMainFedActive ? 'ON' : 'OFF'}
                    </p>
                    <Switch
                      checked={form.isMainFedActive as boolean}
                      onChange={(e) => {
                        toast(
                          <div className="toast-info">
                            <CheckCircleSVG className="w-[16px]" />
                            일괄 정책 선택약정 시세 사용
                            {e.target.checked === false && ' 안함'}
                          </div>,
                          {
                            position: 'bottom-center',
                          },
                        )
                        setForm((prev) => ({
                          ...prev,
                          isMainFedActive: e.target.checked,
                        }))
                      }}
                    />
                  </div>
                }
              >
                <div className="w-full px-3 py-2">
                  <div className="flex w-full flex-col gap-[8px] rounded-[8px] bg-white p-4">
                    <p className="text-[16px] font-bold">
                      *아래 휴대폰 목록에 일괄 적용되는 필수요금제 입력
                    </p>
                    <table className="w-full table-fixed border-collapse">
                      <colgroup>
                        {/* 구분 및 항목 영역 (266px) */}
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '100px' }} />

                        {/* SKT 영역 */}
                        <col style={{ width: '92px' }} />
                        <col />

                        {/* KT 영역 */}
                        <col style={{ width: '92px' }} />
                        <col />

                        {/* LG 영역 */}
                        <col style={{ width: '92px' }} />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className={cn(DEFAULT_TH, 'h-[40px] w-[266px]')}
                            colSpan={2}
                          >
                            구분
                          </th>
                          <th
                            className={cn(
                              SK_TH,
                              'h-[40px] w-[calc((100%-266px)/3)]',
                            )}
                            colSpan={2}
                          >
                            SKT<span className="text-[20px]">*</span>
                          </th>
                          <th
                            className={cn(
                              KT_TH,
                              'h-[40px] w-[calc((100%-266px)/3)]',
                            )}
                            colSpan={2}
                          >
                            KT<span className="text-[20px]">*</span>
                          </th>
                          <th
                            className={cn(
                              LG_TH,
                              'h-[40px] w-[calc((100%-266px)/3)]',
                            )}
                            colSpan={2}
                          >
                            LG U+<span className="text-[20px]">*</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td
                            className={cn(DEFAULT_TH, 'w-[52px] text-center')}
                            rowSpan={2}
                          >
                            공시
                          </td>
                          <td className={cn(DEFAULT_TD, 'text-[11px]')}>
                            필수 요금제 (원)
                          </td>
                          <td
                            className={cn(
                              COLOR_TD,
                              'bg-[#FB5353]/5 text-right',
                            )}
                            colSpan={2}
                            onClick={handleTdClick}
                          >
                            <PriceInput
                              value={form?.skNormalPhdFee}
                              errorField="skNormalPhdFee"
                              onChange={(val) =>
                                setForm((prev) => ({
                                  ...prev,
                                  skNormalPhdFee: val as number,
                                }))
                              }
                            />
                          </td>
                          <td
                            className={cn(
                              COLOR_TD,
                              'bg-[#369EFF0D] text-right',
                            )}
                            colSpan={2}
                            onClick={handleTdClick}
                          >
                            <PriceInput
                              value={form?.ktNormalPhdFee}
                              errorField="ktNormalPhdFee"
                              onChange={(val) =>
                                setForm((prev) => ({
                                  ...prev,
                                  ktNormalPhdFee: val as number,
                                }))
                              }
                            />
                          </td>
                          <td
                            className={cn(
                              COLOR_TD,
                              'bg-[#8539F70D] text-right',
                            )}
                            colSpan={2}
                            onClick={handleTdClick}
                          >
                            <PriceInput
                              value={form?.lgNormalPhdFee}
                              errorField="lgNormalPhdFee"
                              onChange={(val) =>
                                setForm((prev) => ({
                                  ...prev,
                                  lgNormalPhdFee: val as number,
                                }))
                              }
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className={cn(DEFAULT_TD, 'text-[11px]')}>
                            필수 유지기간
                          </td>
                          <td className={cn(DEFAULT_TD, 'w-[92px] px-0 py-0')}>
                            <PeriodSelect
                              value={form?.skNormalPhdPeriodType as PERIOD_TYPE}
                              errorField="skNormalPhdPeriodType"
                              onChange={(value) =>
                                setForm((prev) => ({
                                  ...prev,
                                  skNormalPhdPeriodType: value,
                                }))
                              }
                            />
                          </td>
                          <td
                            className={cn(
                              COLOR_TD,
                              'bg-[#FB5353]/5 text-right',
                            )}
                            onClick={handleTdClick}
                          >
                            <PriceInput
                              value={form?.skNormalPhdUsePeriod}
                              errorField="skNormalPhdUsePeriod"
                              maxLength={
                                form?.skNormalPhdPeriodType !== 'DAY' ? 2 : 3
                              }
                              allowDecimal={false}
                              periodType={
                                form?.skNormalPhdPeriodType as PERIOD_TYPE
                              }
                              onChange={(val) =>
                                setForm((prev) => ({
                                  ...prev,
                                  skNormalPhdUsePeriod: val as any,
                                }))
                              }
                            />
                          </td>
                          <td className={cn(DEFAULT_TD, 'w-[120px] px-0 py-0')}>
                            <PeriodSelect
                              value={form?.ktNormalPhdPeriodType as PERIOD_TYPE}
                              errorField="ktNormalPhdPeriodType"
                              onChange={(value) =>
                                setForm((prev) => ({
                                  ...prev,
                                  ktNormalPhdPeriodType: value,
                                }))
                              }
                            />
                          </td>
                          <td
                            className={cn(
                              COLOR_TD,
                              'bg-[#369EFF0D] text-right',
                            )}
                            onClick={handleTdClick}
                          >
                            <PriceInput
                              value={form?.ktNormalPhdUsePeriod}
                              errorField="ktNormalPhdUsePeriod"
                              maxLength={
                                form?.ktNormalPhdPeriodType !== 'DAY' ? 2 : 3
                              }
                              allowDecimal={false}
                              periodType={
                                form?.ktNormalPhdPeriodType as PERIOD_TYPE
                              }
                              onChange={(val) =>
                                setForm((prev) => ({
                                  ...prev,
                                  ktNormalPhdUsePeriod: val as any,
                                }))
                              }
                            />
                          </td>
                          <td className={cn(DEFAULT_TD, 'w-[120px] px-0 py-0')}>
                            <PeriodSelect
                              value={form?.lgNormalPhdPeriodType as PERIOD_TYPE}
                              errorField="lgNormalPhdPeriodType"
                              onChange={(value) =>
                                setForm((prev) => ({
                                  ...prev,
                                  lgNormalPhdPeriodType: value,
                                }))
                              }
                            />
                          </td>
                          <td
                            className={cn(
                              COLOR_TD,
                              'bg-[#8539F70D] text-right',
                            )}
                            onClick={handleTdClick}
                          >
                            <PriceInput
                              value={form?.lgNormalPhdUsePeriod}
                              errorField="lgNormalPhdUsePeriod"
                              maxLength={
                                form?.lgNormalPhdPeriodType !== 'DAY' ? 2 : 3
                              }
                              allowDecimal={false}
                              periodType={
                                form?.lgNormalPhdPeriodType as PERIOD_TYPE
                              }
                              onChange={(val) =>
                                setForm((prev) => ({
                                  ...prev,
                                  lgNormalPhdUsePeriod: val as any,
                                }))
                              }
                            />
                          </td>
                        </tr>
                        {/* 선약 */}
                        {form.isMainFedActive && (
                          <>
                            <tr>
                              <td
                                className={cn(
                                  DEFAULT_TH,
                                  'w-[52px] text-center',
                                )}
                                rowSpan={2}
                              >
                                선약
                              </td>
                              <td className={cn(DEFAULT_TD, 'text-[11px]')}>
                                필수 요금제 (원)
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#FB5353]/5 text-right',
                                )}
                                colSpan={2}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={form?.skNormalFedFee}
                                  errorField="skNormalFedFee"
                                  onChange={(val) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      skNormalFedFee: val as number,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#369EFF0D] text-right',
                                )}
                                colSpan={2}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={form?.ktNormalFedFee}
                                  errorField="ktNormalFedFee"
                                  onChange={(val) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      ktNormalFedFee: val as number,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#8539F70D] text-right',
                                )}
                                colSpan={2}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={form?.lgNormalFedFee}
                                  errorField="lgNormalFedFee"
                                  onChange={(val) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      lgNormalFedFee: val as number,
                                    }))
                                  }
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className={cn(DEFAULT_TD, 'text-[11px]')}>
                                필수 유지기간
                              </td>
                              <td
                                className={cn(DEFAULT_TD, 'w-[92px] px-0 py-0')}
                              >
                                <PeriodSelect
                                  value={
                                    form?.skNormalFedPeriodType as PERIOD_TYPE
                                  }
                                  errorField="skNormalFedPeriodType"
                                  onChange={(value) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      skNormalFedPeriodType: value,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#FB5353]/5 text-right',
                                )}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={form?.skNormalFedUsePeriod}
                                  errorField="skNormalFedUsePeriod"
                                  maxLength={
                                    form?.skNormalFedPeriodType !== 'DAY'
                                      ? 2
                                      : 3
                                  }
                                  allowDecimal={false}
                                  periodType={
                                    form?.skNormalFedPeriodType as PERIOD_TYPE
                                  }
                                  onChange={(val) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      skNormalFedUsePeriod: val as any,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  DEFAULT_TD,
                                  'w-[120px] px-0 py-0',
                                )}
                              >
                                <PeriodSelect
                                  value={
                                    form?.ktNormalFedPeriodType as PERIOD_TYPE
                                  }
                                  errorField="ktNormalFedPeriodType"
                                  onChange={(value) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      ktNormalFedPeriodType: value,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#369EFF0D] text-right',
                                )}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={form?.ktNormalFedUsePeriod}
                                  errorField="ktNormalFedUsePeriod"
                                  maxLength={
                                    form?.ktNormalFedPeriodType !== 'DAY'
                                      ? 2
                                      : 3
                                  }
                                  allowDecimal={false}
                                  periodType={
                                    form?.ktNormalFedPeriodType as PERIOD_TYPE
                                  }
                                  onChange={(val) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      ktNormalFedUsePeriod: val as any,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  DEFAULT_TD,
                                  'w-[120px] px-0 py-0',
                                )}
                              >
                                <PeriodSelect
                                  value={
                                    form?.lgNormalFedPeriodType as PERIOD_TYPE
                                  }
                                  errorField="lgNormalFedPeriodType"
                                  onChange={(value) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      lgNormalFedPeriodType: value,
                                    }))
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#8539F70D] text-right',
                                )}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={form?.lgNormalFedUsePeriod}
                                  errorField="lgNormalFedUsePeriod"
                                  maxLength={
                                    form?.lgNormalFedPeriodType !== 'DAY'
                                      ? 2
                                      : 3
                                  }
                                  allowDecimal={false}
                                  periodType={
                                    form?.lgNormalFedPeriodType as PERIOD_TYPE
                                  }
                                  onChange={(val) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      lgNormalFedUsePeriod: val as any,
                                    }))
                                  }
                                />
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CoverWrapper>
            </section>

            {/* 주요폰 */}
            <section key={'main-section'} id={`main-section`}>
              <CoverWrapper
                title="주요폰 시세"
                description={`*모든 행은 추가, 삭제, 순서변경 가능해요.
              *무료는 0, 받는 비용은 ‘일반 숫자', 주는 비용(페이백)은 숫자 앞 ‘-’ 기입해주세요.
              *’만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다.
              *해당 휴대폰을 지원하지 않는다면 공란으로 두세요.`}
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) return

                    const oldIndex = (form?.normals || []).findIndex(
                      (items) =>
                        `${items.deviceId}/${items.storage}` == active.id,
                    )
                    const newIndex = (form?.normals || []).findIndex(
                      (items) =>
                        `${items.deviceId}/${items.storage}` == over.id,
                    )

                    let _normal = arrayMove(
                      form?.normals || [],
                      oldIndex,
                      newIndex,
                    ) as newNormalQuotationObject[]

                    setForm((state) => ({ ...state, normals: _normal }))
                  }}
                >
                  <SortableContext
                    items={(form?.normals ?? []).map(
                      (items) => `${items.deviceId}/${items.storage}`,
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <table className="w-full table-auto border-collapse">
                      <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: '144px' }} />
                        <col style={{ width: '72px' }} />
                        <col style={{ width: '52px' }} />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                        <col style={{ width: '40px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className={cn(DEFAULT_TH, 'h-[72px]')}
                            rowSpan={2}
                          >
                            순서
                          </th>
                          <th className={cn(DEFAULT_TH)} rowSpan={2}>
                            휴대폰
                          </th>
                          <th className={cn(DEFAULT_TH)} rowSpan={2}>
                            용량
                          </th>
                          <th
                            className={cn(DEFAULT_TH, 'w-[52px] text-center')}
                            rowSpan={2}
                          >
                            구분
                          </th>
                          <th className={cn(SK_TH, 'h-[36px]')} colSpan={2}>
                            SKT
                          </th>
                          <th className={cn(KT_TH)} colSpan={2}>
                            KT
                          </th>
                          <th className={cn(LG_TH)} colSpan={2}>
                            LG U+
                          </th>
                          <th className={cn(DEFAULT_TH)} rowSpan={2}>
                            삭제
                          </th>
                        </tr>
                        <tr>
                          <th className={cn(DEFAULT_TH)}>번이(만원)</th>
                          <th className={cn(DEFAULT_TH)}>기변(만원)</th>
                          <th className={cn(DEFAULT_TH)}>번이(만원)</th>
                          <th className={cn(DEFAULT_TH)}>기변(만원)</th>
                          <th className={cn(DEFAULT_TH)}>번이(만원)</th>
                          <th className={cn(DEFAULT_TH)}>기변(만원)</th>
                        </tr>
                      </thead>

                      {isSuccess &&
                        (form?.normals || []).map(
                          (items: newNormalQuotationObject, index) => {
                            return (
                              <DraggableRow
                                key={`${items.deviceId}/${items.storage}`}
                                id={`${items.deviceId}/${items.storage}`}
                              >
                                <tr>
                                  <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                    <div className="flex items-center justify-center">
                                      <img
                                        src="/static/icons/icon_recoder.svg"
                                        className="h-[14px] w-[14px] shrink-0"
                                        alt="recorder icon"
                                      />
                                    </div>
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'font-medium')}
                                    rowSpan={2}
                                  >
                                    {items.deviceName || '-'}
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'font-medium')}
                                    rowSpan={2}
                                  >
                                    {items.storage}
                                  </td>
                                  <td
                                    className={cn(
                                      DEFAULT_TH,
                                      'min-w-[52px] text-center',
                                    )}
                                  >
                                    공시
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FB5353]/5 text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="skNcPhdPrice"
                                      value={items.skNcPhdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateMainItem(
                                          index,
                                          'skNcPhdPrice',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FB5353]/5 text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="skCdPhdPrice"
                                      value={items.skCdPhdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateMainItem(
                                          index,
                                          'skCdPhdPrice',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#369EFF0D] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="ktNcPhdPrice"
                                      value={items.ktNcPhdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateMainItem(
                                          index,
                                          'ktNcPhdPrice',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#369EFF0D] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="ktCdPhdPrice"
                                      value={items.ktCdPhdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateMainItem(
                                          index,
                                          'ktCdPhdPrice',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#8539F70D] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="lgNcPhdPrice"
                                      value={items.lgNcPhdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateMainItem(
                                          index,
                                          'lgNcPhdPrice',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#8539F70D] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="lgCdPhdPrice"
                                      value={items.lgCdPhdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateMainItem(
                                          index,
                                          'lgCdPhdPrice',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                    <div
                                      className="flex cursor-pointer items-center justify-center"
                                      onPointerDown={(e) => {
                                        e.stopPropagation()
                                      }}
                                      onClick={(e) => {
                                        const deleteExecute = () => {
                                          setForm((prev) => ({
                                            ...prev,
                                            normals: (
                                              prev?.normals || []
                                            ).filter(
                                              ({ deviceId, storage }) =>
                                                `${items.deviceId}/${items.storage}` !==
                                                `${deviceId}/${storage}`,
                                            ),
                                          }))
                                        }
                                        deletePopoverClick(e, deleteExecute)
                                      }}
                                    >
                                      <img
                                        src="/static/icons/icon_delete.svg"
                                        className="h-[14px] w-[14px] shrink-0"
                                        alt="icon_delete"
                                      />
                                    </div>
                                  </td>
                                </tr>
                                {form.isMainFedActive && (
                                  <tr>
                                    <td
                                      className={cn(DEFAULT_TH, 'text-center')}
                                    >
                                      선약
                                    </td>
                                    <td
                                      className={cn(
                                        COLOR_TD,
                                        'bg-[#FB5353]/5 text-right',
                                      )}
                                      onClick={handleTdClick}
                                    >
                                      <PriceInput
                                        errorField="skNcFedPrice"
                                        value={items.skNcFedPrice}
                                        suffix="만원"
                                        allowDecimal
                                        maxLength={5}
                                        onChange={(val) =>
                                          updateMainItem(
                                            index,
                                            'skNcFedPrice',
                                            val,
                                          )
                                        }
                                      />
                                    </td>
                                    <td
                                      className={cn(
                                        COLOR_TD,
                                        'bg-[#FB5353]/5 text-right',
                                      )}
                                      onClick={handleTdClick}
                                    >
                                      <PriceInput
                                        errorField="skCdFedPrice"
                                        value={items.skCdFedPrice}
                                        suffix="만원"
                                        allowDecimal
                                        maxLength={5}
                                        onChange={(val) =>
                                          updateMainItem(
                                            index,
                                            'skCdFedPrice',
                                            val,
                                          )
                                        }
                                      />
                                    </td>
                                    <td
                                      className={cn(
                                        COLOR_TD,
                                        'bg-[#369EFF0D] text-right',
                                      )}
                                      onClick={handleTdClick}
                                    >
                                      <PriceInput
                                        errorField="ktNcFedPrice"
                                        value={items.ktNcFedPrice}
                                        suffix="만원"
                                        allowDecimal
                                        maxLength={5}
                                        onChange={(val) =>
                                          updateMainItem(
                                            index,
                                            'ktNcFedPrice',
                                            val,
                                          )
                                        }
                                      />
                                    </td>
                                    <td
                                      className={cn(
                                        COLOR_TD,
                                        'bg-[#369EFF0D] text-right',
                                      )}
                                      onClick={handleTdClick}
                                    >
                                      <PriceInput
                                        errorField="ktCdFedPrice"
                                        value={items.ktCdFedPrice}
                                        suffix="만원"
                                        allowDecimal
                                        maxLength={5}
                                        onChange={(val) =>
                                          updateMainItem(
                                            index,
                                            'ktCdFedPrice',
                                            val,
                                          )
                                        }
                                      />
                                    </td>
                                    <td
                                      className={cn(
                                        COLOR_TD,
                                        'bg-[#8539F70D] text-right',
                                      )}
                                      onClick={handleTdClick}
                                    >
                                      <PriceInput
                                        errorField="lgNcFedPrice"
                                        value={items.lgNcFedPrice}
                                        suffix="만원"
                                        allowDecimal
                                        maxLength={5}
                                        onChange={(val) =>
                                          updateMainItem(
                                            index,
                                            'lgNcFedPrice',
                                            val,
                                          )
                                        }
                                      />
                                    </td>
                                    <td
                                      className={cn(
                                        COLOR_TD,
                                        'bg-[#8539F70D] text-right',
                                      )}
                                      onClick={handleTdClick}
                                    >
                                      <PriceInput
                                        errorField="lgCdFedPrice"
                                        value={items.lgCdFedPrice}
                                        suffix="만원"
                                        allowDecimal
                                        maxLength={5}
                                        onChange={(val) =>
                                          updateMainItem(
                                            index,
                                            'lgCdFedPrice',
                                            val,
                                          )
                                        }
                                      />
                                    </td>
                                  </tr>
                                )}
                              </DraggableRow>
                            )
                          },
                        )}
                    </table>
                  </SortableContext>
                </DndContext>
              </CoverWrapper>
              <Button
                variant="primary"
                className="float-right mt-[10px]"
                onClick={() => {
                  let _used: {
                    deviceId: number
                    deviceName: string
                    type: string
                    storage: string
                  }[] = []
                    ; (form?.normals || []).map((item) => {
                      _used.push({
                        deviceId: item.deviceId,
                        deviceName: item.deviceName,
                        type: 'REMAIN',
                        storage: item.storage,
                      })
                    })
                  openModal(MODAL_KEY.DeviceMainSelect, {
                    title: '주요폰',
                    used: _used as normalQuotationObject[],
                    category: 'MAIN',
                    source: isPartner ? 'partner' : 'store',
                    onSelected: (_selected: any[]) => {
                      let _item = [] as any
                      _selected.map((item) =>
                        _item.push({
                          deviceId: item?.deviceId || '',
                          deviceName: item?.deviceName || '',
                          storage: item?.storage || '',
                        }),
                      )

                      setForm((prev) => ({
                        ...prev,
                        normals: [...(prev.normals || []), ..._item],
                      }))
                      SuccessSnackbar()
                    },
                  })
                }}
                label="항목 추가"
              />
            </section>

            {/* 동시판매 추가 할인금 */}
            <section key={'bundle-section'} id={`bundle-section`}>
              <CoverWrapper
                title="[동시판매시] 휴대폰 추가 할인금"
                description={`*인터넷 상품은 1GB 기준으로 인터넷만 동시 구매 했을때, 인터넷+TV 동시 구매 했을때 할인금을 적어주세요. 위 주요폰 시세표에 입력하신 휴대폰 최종 구매가에서 차감되어 보여집니다.
  (ex. 1GB 인터넷만 동시 구매, 번호이동, 30만원 추가 할인 입력시 > 갤럭시 S26 번호이동 50만원 적었다면 > 동시 구매 최종 구매가 : 50 - 30 = 20만원)
*최소 유지기간은 'M+13개월', 약정은 '3년 약정' 기준으로 판매점에서 할인해줄 금액을 '숫자'로만 기입해주세요.
*할인금 입력시 기본은 '만원' 단위이며 소수점 한자리('천원' 단위)까지 입력이 가능합니다.
*지원하지 않는 상품은 '공란'으로 두시면, 고객에게 보여지지 않습니다.`}
              >
                <BundleSaleTable
                  items={form?.bundleSales ?? initBundleSales()}
                  onChange={(bundleSales) =>
                    setForm((prev) => ({ ...prev, bundleSales }))
                  }
                />
              </CoverWrapper>
            </section>

            {/* 중저가폰 */}
            {!isSectionHidden('lows') && (
            <section key={'lows-section'} id={`lows-section`}>
              <CoverWrapper
                title="휴대폰별 별도 정책 가격입력"
                description={`*일괄 적용되는 필수요금제가 아닌 다른 요금제를 적용하고 싶은 경우 활용하세요.
*모든 행은 추가, 삭제, 순서변경 가능해요.
*무료는 0, 받는 비용은 ‘일반 숫자', 주는 비용(페이백)은 숫자 앞 ‘-’ 기입해주세요.
*’만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다.
*선택약정 필수 요금제 조건은 꼭 ‘할인 전' 금액으로 입력해주세요. (자동으로 25%할인 표기되어 보여집니다.)
*해당 휴대폰을 지원하지 않는다면 공란으로 두세요.`}
                titleChildren={
                  <div className="w-full flex-row justify-baseline">
                    <p
                      className={cn(
                        'text-[14px] font-semibold',
                        form.isLowFedActive
                          ? 'text-primary-50'
                          : 'text-gray-40',
                      )}
                    >
                      선택약정 시세표 {form.isLowFedActive ? 'ON' : 'OFF'}
                    </p>
                    <Switch
                      checked={form.isLowFedActive as boolean}
                      onChange={(e) => {
                        toast(
                          <div className="toast-info">
                            <CheckCircleSVG className="w-[16px]" />
                            휴대폰 별도 정책 선택약정 시세 사용
                            {e.target.checked === false && ' 안함'}
                          </div>,
                          {
                            position: 'bottom-center',
                          },
                        )
                        setForm((prev) => ({
                          ...prev,
                          isLowFedActive: e.target.checked,
                        }))
                      }}
                    />
                  </div>
                }
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) return
                    const oldIndex = rowsWithStableIds.findIndex(
                      (items) => `${items.deviceId}/${items.uuid}` == active.id,
                    )
                    const newIndex = rowsWithStableIds.findIndex(
                      (items) => `${items.deviceId}/${items.uuid}` == over.id,
                    )
                    let _lows = arrayMove(
                      form?.lows || [],
                      oldIndex,
                      newIndex,
                    ) as newLowsQuotationObject[]
                    setForm((state) => ({ ...state, lows: _lows }))
                  }}
                >
                  <SortableContext
                    items={rowsWithStableIds.map(
                      (items) => `${items.deviceId}/${items.uuid}`,
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <table className="w-full table-fixed border-collapse">
                      <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: '144px' }} />
                        <col style={{ width: '72px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '100px' }} />
                        <col />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '48px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          {LOW_HEADER.map(
                            ({ label, colspan, require }, index) => (
                              <th
                                key={index}
                                className={cn(DEFAULT_TH, 'h-[56px]')}
                                colSpan={colspan}
                              >
                                <div className="flex-row justify-center">
                                  {label}
                                  {require && (
                                    <span className="text-[20px]">*</span>
                                  )}
                                </div>
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      {isSuccess &&
                        rowsWithStableIds.map((items, index) => {
                          return (
                            <DraggableRow
                              key={`${items.deviceId}/${items.uuid}`}
                              id={`${items.deviceId}/${items.uuid}`}
                            >
                              <tr>
                                <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                  <div className="flex items-center justify-center">
                                    <img
                                      src="/static/icons/icon_recoder.svg"
                                      className="h-[14px] w-[14px] shrink-0"
                                      alt="recorder icon"
                                    />
                                  </div>
                                </td>
                                <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                  {items.deviceName}
                                </td>
                                <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                  {items.storage}
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'px-0 py-0')}
                                  rowSpan={2}
                                >
                                  <Select
                                    className="w-full text-[11px]"
                                    disableUnderline
                                    SelectDisplayProps={
                                      {
                                        'data-error-field': 'telecom',
                                        'data-error-value': items.telecom ?? '',
                                      } as React.HTMLAttributes<HTMLDivElement>
                                    }
                                    MenuProps={{
                                      disablePortal: true,
                                      MenuListProps: {
                                        disablePadding: true,
                                        onMouseDown: (e) => e.stopPropagation(),
                                      },
                                    }}
                                    sx={{
                                      fontSize: '11px',
                                      fontWeight: 'medium',
                                      '& fieldset': {
                                        border: 'none',
                                      },
                                    }}
                                    variant="outlined"
                                    value={items.telecom}
                                    onPointerDown={(e) => e.stopPropagation()} // 💡 드래그 이벤트 방지
                                    onMouseDown={(e) => e.stopPropagation()} // 💡 드래그 이벤트 방지
                                    onTouchStart={(e) => e.stopPropagation()} // 💡 모바일 대응
                                    onChange={(e) =>
                                      updateLowsItem(
                                        index,
                                        'telecom',
                                        e.target.value,
                                      )
                                    }
                                  >
                                    {['SK', 'KT', 'LG'].map((tel) => {
                                      const selectedTelecoms =
                                        getSelectedTelecomsByDevice(
                                          items.deviceId,
                                          'lows',
                                          items.storage,
                                        )
                                      const isSelf = items.telecom === tel
                                      const alreadyUsed =
                                        selectedTelecoms.includes(tel)

                                      return (
                                        <MenuItem
                                          key={tel}
                                          value={tel}
                                          sx={{
                                            fontSize: '11px',
                                            fontWeight: 'medium',
                                          }}
                                          disabled={!isSelf && alreadyUsed}
                                        >
                                          {tel === 'SK'
                                            ? 'SKT'
                                            : tel === 'KT'
                                              ? 'KT'
                                              : 'LG U+'}
                                        </MenuItem>
                                      )
                                    })}
                                  </Select>
                                </td>
                                <td
                                  className={cn(
                                    DEFAULT_TH,
                                    'w-[52px] text-center',
                                  )}
                                >
                                  공시
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] text-right',
                                  )}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.phdFee}
                                    errorField="phdFee"
                                    maxLength={7}
                                    allowDecimal={false}
                                    onChange={(val) =>
                                      updateLowsItem(
                                        index,
                                        'phdFee',
                                        val as number,
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] px-0 py-0',
                                  )}
                                >
                                  <PeriodSelect
                                    value={items.phdPeriodType as PERIOD_TYPE}
                                    errorField="phdPeriodType"
                                    onChange={(val) =>
                                      updateLowsItem(
                                        index,
                                        'phdPeriodType',
                                        val,
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] text-right',
                                  )}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.phdUsePeriod}
                                    errorField="phdUsePeriod"
                                    maxLength={
                                      items.phdPeriodType !== 'DAY' ? 2 : 3
                                    }
                                    allowDecimal={false}
                                    periodType={
                                      items.phdPeriodType as PERIOD_TYPE
                                    }
                                    onChange={(val) =>
                                      updateLowsItem(index, 'phdUsePeriod', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'text-right')}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    errorField="ncPhdPrice"
                                    value={items.ncPhdPrice}
                                    maxLength={5}
                                    allowDecimal={true}
                                    suffix="만원"
                                    onChange={(val) =>
                                      updateLowsItem(index, 'ncPhdPrice', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'text-right')}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    errorField="cdPhdPrice"
                                    value={items.cdPhdPrice}
                                    maxLength={5}
                                    allowDecimal={true}
                                    suffix="만원"
                                    onChange={(val) =>
                                      updateLowsItem(index, 'cdPhdPrice', val)
                                    }
                                  />
                                </td>
                                <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                  <div
                                    className="flex cursor-pointer items-center justify-center"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      const deleteExecute = () => {
                                        setForm((prev) => ({
                                          ...prev,
                                          lows: (prev?.lows || []).filter(
                                            (_, filterIndex) =>
                                              index !== filterIndex,
                                          ),
                                        }))
                                      }
                                      deletePopoverClick(e, deleteExecute)
                                    }}
                                  >
                                    <img
                                      src="/static/icons/icon_delete.svg"
                                      className="h-[14px] w-[14px] shrink-0"
                                      alt="icon_delete"
                                    />
                                  </div>
                                </td>
                              </tr>
                              {form.isLowFedActive && (
                                <tr>
                                  <td className={cn(DEFAULT_TH, 'text-center')}>
                                    선약
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FFFCE9] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      value={items.fedFee}
                                      errorField="fedFee"
                                      maxLength={7}
                                      allowDecimal={false}
                                      onChange={(val) =>
                                        updateLowsItem(
                                          index,
                                          'fedFee',
                                          val as number,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FFFCE9] px-0 py-0',
                                    )}
                                  >
                                    <PeriodSelect
                                      value={items.fedPeriodType as PERIOD_TYPE}
                                      errorField="fedPeriodType"
                                      onChange={(val) =>
                                        updateLowsItem(
                                          index,
                                          'fedPeriodType',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FFFCE9] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      value={items.fedUsePeriod}
                                      errorField="fedUsePeriod"
                                      maxLength={
                                        items.fedPeriodType !== 'DAY' ? 2 : 3
                                      }
                                      periodType={
                                        items.fedPeriodType as PERIOD_TYPE
                                      }
                                      allowDecimal={false}
                                      onChange={(val) =>
                                        updateLowsItem(
                                          index,
                                          'fedUsePeriod',
                                          val,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'text-right')}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="ncFedPrice"
                                      value={items.ncFedPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateLowsItem(index, 'ncFedPrice', val)
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'text-right')}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="cdFedPrice"
                                      value={items.cdFedPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateLowsItem(index, 'cdFedPrice', val)
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </DraggableRow>
                          )
                        })}
                    </table>
                  </SortableContext>
                </DndContext>
              </CoverWrapper>
              <Button
                variant="primary"
                className="float-right mt-[10px]"
                onClick={() => {
                  openModal(MODAL_KEY.DeviceDefaultSelect, {
                    title: '중저가폰',
                    used: form.lows,
                    category: 'LOW',
                    onSelected: (_selected: any[]) => {
                      let _item = [] as any
                      _selected.map((item) =>
                        _item.push({
                          uuid: uuidv4(),
                          deviceId: item?.deviceId || '',
                          deviceName: item?.deviceName || '',
                          storage: item?.storage || '',
                          isMonth: true,
                          phdPeriodType: 'MONTH',
                          fedPeriodType: 'MONTH',
                        }),
                      )

                      setForm((prev) => ({
                        ...prev,
                        lows: [...(prev.lows || []), ..._item],
                      }))
                      SuccessSnackbar()
                    },
                  })
                }}
                label="항목 추가"
              />
            </section>
            )}

            {form.isDpSelling && (
              <section key={'dps-section'} id={`dps-section`}>
                <CoverWrapper
                  title="디피폰 시세"
                  description={`*모든 행은 추가, 삭제, 순서변경 가능해요.
  *무료는 0, 받는 비용은 ‘일반 숫자', 주는 비용(페이백)은 숫자 앞 ‘-’ 기입해주세요.
  *’만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다.
  *해당 휴대폰을 지원하지 않는다면 공란으로 두세요.`}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={({ active, over }) => {
                      if (!over || active.id === over.id) return
                      const oldIndex = rowsWithStableIdsDps.findIndex(
                        (items) =>
                          `${items.deviceId}/${items.uuid}` == active.id,
                      )
                      const newIndex = rowsWithStableIdsDps.findIndex(
                        (items) => `${items.deviceId}/${items.uuid}` == over.id,
                      )
                      let _dps = arrayMove(
                        form?.dps || [],
                        oldIndex,
                        newIndex,
                      ) as dpsQuotationObject[]
                      setForm((state) => ({ ...state, dps: _dps }))
                    }}
                  >
                    <SortableContext
                      items={rowsWithStableIdsDps.map(
                        (items) => `${items.deviceId}/${items.uuid}`,
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      <table className="w-full table-fixed border-collapse">
                        <colgroup>
                          <col style={{ width: '40px' }} />
                          <col style={{ width: '144px' }} />
                          <col style={{ width: '72px' }} />
                          <col style={{ width: '80px' }} />
                          <col style={{ width: '72px' }} />
                          <col />
                          <col style={{ width: '100px' }} />
                          <col style={{ width: '144px' }} />
                          <col />
                          <col />
                          <col style={{ width: '60px' }} />
                          <col style={{ width: '40px' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            {DP_HEADER.map(
                              ({ label, colspan, require }, index) => (
                                <th
                                  key={index}
                                  className={cn(DEFAULT_TH, 'h-[56px]')}
                                  colSpan={colspan}
                                >
                                  <div className="flex-row justify-center">
                                    {label}
                                    {require && (
                                      <span className="text-[20px]">*</span>
                                    )}
                                  </div>
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>

                        {isSuccess &&
                          rowsWithStableIdsDps.map((items, index) => {
                            return (
                              <DraggableRow
                                key={`${items.deviceId}/${items.uuid}`}
                                id={`${items.deviceId}/${items.uuid}`}
                              >
                                <tr>
                                  <td className={cn(DEFAULT_TD)}>
                                    <div className="flex items-center justify-center">
                                      <img
                                        src="/static/icons/icon_recoder.svg"
                                        className="h-[14px] w-[14px] shrink-0"
                                        alt="recorder icon"
                                      />
                                    </div>
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'min-w-[144px]')}
                                  >
                                    {items.deviceName}
                                  </td>
                                  <td className={cn(DEFAULT_TD)}>
                                    {items.storage}
                                  </td>
                                  <td className={cn(DEFAULT_TD)}>
                                    {items.color}
                                  </td>
                                  <td className={cn(DEFAULT_TD, 'p-0')}>
                                    <Select
                                      className="w-full text-[11px]"
                                      disableUnderline
                                      MenuProps={{
                                        disablePortal: true,
                                        MenuListProps: {
                                          disablePadding: true,
                                          onMouseDown: (e) =>
                                            e.stopPropagation(),
                                        },
                                      }}
                                      sx={{
                                        fontSize: '11px',
                                        fontWeight: 'medium',
                                        '& fieldset': {
                                          border: 'none',
                                        },
                                        '& .MuiSelect-select': {
                                          paddingY: '8px !important',
                                          paddingX: '12px !important',
                                          display: 'flex',
                                          alignItems: 'center',
                                        },
                                        '& .MuiSelect-icon': {
                                          right: '4px',
                                        },
                                      }}
                                      variant="outlined"
                                      value={items.telecom}
                                      onMouseDown={(e) => e.stopPropagation()} // 💡 드래그 이벤트 방지
                                      onPointerDown={(e) => e.stopPropagation()} // 💡 모바일 대응
                                      onTouchStart={(e) => e.stopPropagation()} // 💡 모바일 대응
                                      onChange={(e) =>
                                        updateDpsItem(
                                          index,
                                          'telecom',
                                          e.target.value,
                                        )
                                      }
                                    >
                                      {['SK', 'KT', 'LG'].map((tel) => {
                                        const selectedTelecoms =
                                          getSelectedTelecomsByDevice(
                                            items.deviceId,
                                            'dps',
                                            items.storage,
                                            items.color,
                                          )
                                        const isSelf = items.telecom === tel
                                        const alreadyUsed =
                                          selectedTelecoms.includes(tel)

                                        return (
                                          <MenuItem
                                            key={tel}
                                            value={tel}
                                            disabled={!isSelf && alreadyUsed}
                                            sx={{
                                              fontSize: '11px',
                                              fontWeight: 'medium',
                                            }}
                                          >
                                            {tel === 'SK'
                                              ? 'SKT'
                                              : tel === 'KT'
                                                ? 'KT'
                                                : 'LG U+'}
                                          </MenuItem>
                                        )
                                      })}
                                    </Select>
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FFFCE9] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="fee"
                                      value={items.fee}
                                      maxLength={7}
                                      allowDecimal={false}
                                      onChange={(val) =>
                                        updateDpsItem(
                                          index,
                                          'fee',
                                          val as number,
                                        )
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(COLOR_TD, 'bg-[#FFFCE9] p-0')}
                                  >
                                    <PeriodSelect
                                      value={items.periodType as PERIOD_TYPE}
                                      onChange={(val) =>
                                        updateDpsItem(index, 'periodType', val)
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(
                                      COLOR_TD,
                                      'bg-[#FFFCE9] text-right',
                                    )}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="usePeriod"
                                      value={items.usePeriod}
                                      maxLength={
                                        items.periodType === 'DAY' ? 3 : 2
                                      }
                                      periodType={
                                        items.periodType as PERIOD_TYPE
                                      }
                                      allowDecimal={false}
                                      onChange={(val) =>
                                        updateDpsItem(index, 'usePeriod', val)
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'text-right')}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="ncPrice"
                                      value={items.ncPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateDpsItem(index, 'ncPrice', val)
                                      }
                                    />
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD, 'text-right')}
                                    onClick={handleTdClick}
                                  >
                                    <PriceInput
                                      errorField="cdPrice"
                                      value={items.cdPrice}
                                      maxLength={5}
                                      allowDecimal={true}
                                      suffix="만원"
                                      onChange={(val) =>
                                        updateDpsItem(index, 'cdPrice', val)
                                      }
                                    />
                                  </td>
                                  <td className={cn(DEFAULT_TD, 'py-0')}
                                    onMouseDown={(e) => e.stopPropagation()} // 💡 드래그 이벤트 방지
                                    onPointerDown={(e) => e.stopPropagation()} // 💡 모바일 대응
                                    onTouchStart={(e) => e.stopPropagation()} // 💡 모바일 대응
                                  >
                                    <div className="flex justify-center">
                                      <Switch
                                        checked={items.isActive}
                                        value={items.isActive}
                                        onChange={
                                          () =>
                                            postDpActiveMutation.mutate({
                                              elementId: items.elementId,
                                              isActive: !items.isActive,
                                            })
                                          // updateDpsItem(
                                          //   index,
                                          //   "isActive",
                                          //   !items.isActive
                                          // )
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td
                                    className={cn(DEFAULT_TD)}
                                    onPointerDown={(e) => e.stopPropagation()}
                                  >
                                    <div
                                      className="flex cursor-pointer items-center justify-center"
                                      onClick={(e) => {
                                        const deleteExecute = () => {
                                          setForm((prev) => ({
                                            ...prev,
                                            dps: (prev?.dps || []).filter(
                                              (_, filterIndex) =>
                                                index !== filterIndex,
                                            ),
                                          }))
                                        }
                                        deletePopoverClick(e, deleteExecute)
                                      }}
                                    >
                                      <img
                                        src="/static/icons/icon_delete.svg"
                                        className="h-[14px] w-[14px] shrink-0"
                                        alt="icon_delete"
                                      />
                                    </div>
                                  </td>
                                </tr>
                              </DraggableRow>
                            )
                          })}
                      </table>
                    </SortableContext>
                  </DndContext>
                </CoverWrapper>
                <Button
                  variant="primary"
                  className="float-right mt-[10px]"
                  onClick={() => {
                    openModal(MODAL_KEY.DeviceDPSelect, {
                      title: '디피폰',
                      used: form.dps,
                      category: 'ALL',
                      onSelected: (_selected: any[]) => {
                        let _item = [] as any
                        _selected.map((item) =>
                          _item.push({
                            uuid: uuidv4(),
                            deviceId: item?.deviceId || '',
                            deviceName: item?.deviceName || '',
                            color: item?.color || '',
                            storage: item?.storage || '',
                            isMonth: true,
                          }),
                        )

                        setForm((prev) => ({
                          ...prev,
                          dps: [...(prev.dps || []), ..._item],
                        }))
                        SuccessSnackbar()
                      },
                    })
                  }}
                  label="항목 추가"
                />
              </section>
            )}

            {/* 키즈폰 */}
            <section key={'kids-section'} id={`kids-section`}>
              <CoverWrapper
                title="키즈폰 시세(가족결합 시 조건)"
                description={`*키즈폰은 가족결합 조건으로 작성해주세요.
                *모든 행은 추가, 삭제, 순서변경 가능해요.
*무료는 0, 받는 비용은 ‘일반 숫자', 주는 비용(페이백)은 숫자 앞 ‘-’ 기입해주세요.
*부가서비스를 가입하지 않아도 되는 기종이면 ‘부가서비스 적용 여부'를 Off 시켜주세요.
*’만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다.
*해당 휴대폰을 지원하지 않는다면 공란으로 두세요.`}
                titleChildren={
                  <div className="flex-row-between w-full">
                    <div className="flex-row">
                      <p
                        className={cn(
                          'text-[14px] font-semibold',
                          form.isKidsFedActive
                            ? 'text-primary-50'
                            : 'text-gray-40',
                        )}
                      >
                        선택약정 시세표 {form.isKidsFedActive ? 'ON' : 'OFF'}
                      </p>
                      <Switch
                        checked={form.isKidsFedActive as boolean}
                        onChange={(e) => {
                          toast(
                            <div className="toast-info">
                              <CheckCircleSVG className="w-[16px]" />
                              키즈폰 정책 선택약정 시세 사용
                              {e.target.checked === false && ' 안함'}
                            </div>,
                            {
                              position: 'bottom-center',
                            },
                          )
                          setForm((prev) => ({
                            ...prev,
                            isKidsFedActive: e.target.checked,
                          }))
                        }}
                      />
                    </div>
                    <div className="flex-row">
                      <p className="text-style-compact-02 pr-2 font-normal text-gray-50">
                        (부가서비스가 없다면 우측 버튼을 비활성화 해주세요.)
                      </p>
                      <p className="text-style-body-03 font-semibold">
                        부가서비스 사용
                      </p>
                      <Switch
                        checked={form.isKidsAdditionalServiceUse}
                        onChange={(e) => {
                          toast(
                            <div className="toast-info">
                              <CheckCircleSVG className="w-[16px]" />
                              키즈폰 부가서비스 사용
                              {e.target.checked === false && ' 안함'}
                            </div>,
                            {
                              position: 'bottom-center',
                            },
                          )
                          setForm({
                            ...form,
                            isKidsAdditionalServiceUse: e.target.checked,
                          })
                        }}
                      />
                    </div>
                  </div>
                }
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) return
                    const oldIndex = (form?.kids || []).findIndex(
                      (items) => `${items.deviceId}/${items.uuid}` == active.id,
                    )
                    const newIndex = (form?.kids || []).findIndex(
                      (items) => `${items.deviceId}/${items.uuid}` == over.id,
                    )
                    let _kids = arrayMove(
                      form?.kids || [],
                      oldIndex,
                      newIndex,
                    ) as newKidsQuotationObject[]
                    setForm((state) => ({ ...state, kids: _kids }))
                  }}
                >
                  <SortableContext
                    items={(form?.kids ?? []).map(
                      (items) => `${items.deviceId}/${items.uuid}`,
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    <table className="w-full table-fixed border-collapse">
                      <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '72px' }} />
                        <col style={{ width: '72px' }} />
                        <col style={{ width: '52px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '160px' }} />
                        <col />
                        <col />
                        <col />
                        <col style={{ width: '40px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          {KIDS_HEADER.map(
                            ({ label, colspan, require }, index) => (
                              <th
                                key={index}
                                className={cn(DEFAULT_TH, 'h-[56px]')}
                                colSpan={colspan}
                              >
                                <div className="flex-row justify-center">
                                  {label}
                                  {require && (
                                    <span className="text-[20px]">*</span>
                                  )}
                                </div>
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      {isSuccess &&
                        (form?.kids || []).map((items, index) => (
                          <DraggableRow
                            key={`${items.deviceId}/${items.uuid}`}
                            id={`${items.deviceId}/${items.uuid}`}
                          >
                            <tr>
                              <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                <div className="flex items-center justify-center">
                                  <img
                                    src="/static/icons/icon_recoder.svg"
                                    className="h-[14px] w-[14px] shrink-0"
                                    alt="recorder icon"
                                  />
                                </div>
                              </td>
                              <td className={cn(DEFAULT_TD)} rowSpan={2}>
                                {items.deviceName}
                              </td>
                              <td
                                className={cn(DEFAULT_TD, 'font-medium')}
                                rowSpan={2}
                              >
                                {items.storage}
                              </td>
                              <td className={cn(DEFAULT_TD, 'p-0')} rowSpan={2}>
                                <Select
                                  className="w-full"
                                  disableUnderline
                                  SelectDisplayProps={
                                    {
                                      'data-error-field': 'telecom',
                                      'data-error-value': items.telecom ?? '',
                                    } as React.HTMLAttributes<HTMLDivElement>
                                  }
                                  MenuProps={{
                                    disablePortal: true,
                                    MenuListProps: {
                                      disablePadding: true,
                                      onMouseDown: (e) => e.stopPropagation(),
                                    },
                                  }}
                                  sx={{
                                    height: '40px',
                                    fontSize: '11px',
                                    fontWeight: 'medium',
                                    '& fieldset': {
                                      border: 'none',
                                    },
                                    '& .MuiSelect-select': {
                                      paddingY: '8px !important',
                                      paddingX: '12px !important',
                                      display: 'flex',
                                      alignItems: 'center',
                                    },
                                    '& .MuiSelect-icon': {
                                      right: '4px',
                                    },
                                  }}
                                  variant="outlined"
                                  value={items.telecom}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    updateKidsItem(
                                      index,
                                      'telecom',
                                      e.target.value,
                                    )
                                  }
                                >
                                  {['SK', 'KT', 'LG'].map((tel) => {
                                    const selectedTelecoms =
                                      getSelectedTelecomsByDevice(
                                        items.deviceId,
                                        'kids',
                                      )
                                    const isSelf = items.telecom === tel
                                    const alreadyUsed =
                                      selectedTelecoms.includes(tel)

                                    return (
                                      <MenuItem
                                        key={tel}
                                        value={tel}
                                        sx={{
                                          fontSize: '11px',
                                          fontWeight: 'medium',
                                        }}
                                        disabled={!isSelf && alreadyUsed}
                                      >
                                        {tel === 'SK'
                                          ? 'SKT'
                                          : tel === 'KT'
                                            ? 'KT'
                                            : 'LG U+'}
                                      </MenuItem>
                                    )
                                  })}
                                </Select>
                              </td>
                              <td
                                className={cn(
                                  DEFAULT_TH,
                                  'w-[52px] text-center',
                                )}
                              >
                                공시
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#FFFCE9] text-right',
                                )}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={items.phdFee}
                                  errorField="phdFee"
                                  maxLength={7}
                                  allowDecimal={false}
                                  onChange={(val) =>
                                    updateKidsItem(
                                      index,
                                      'phdFee',
                                      val as number,
                                    )
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#FFFCE9] px-0 py-0',
                                )}
                                onClick={handleTdClick}
                              >
                                <PeriodSelect
                                  value={items.phdPeriodType as PERIOD_TYPE}
                                  errorField="phdPeriodType"
                                  onChange={(val) =>
                                    updateKidsItem(index, 'phdPeriodType', val)
                                  }
                                />
                              </td>
                              <td
                                className={cn(
                                  COLOR_TD,
                                  'bg-[#FFFCE9] text-right',
                                )}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  value={items.phdUsePeriod}
                                  errorField="phdUsePeriod"
                                  maxLength={
                                    items.phdPeriodType !== 'DAY' ? 2 : 3
                                  }
                                  allowDecimal={false}
                                  periodType={
                                    items.phdPeriodType as PERIOD_TYPE
                                  }
                                  onChange={(val) =>
                                    updateKidsItem(index, 'phdUsePeriod', val)
                                  }
                                />
                              </td>
                              <td
                                className={cn(DEFAULT_TD, 'text-right')}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  errorField="naPhdPrice"
                                  value={items.naPhdPrice}
                                  maxLength={5}
                                  allowDecimal={true}
                                  suffix="만원"
                                  onChange={(val) =>
                                    updateKidsItem(index, 'naPhdPrice', val)
                                  }
                                />
                              </td>
                              <td
                                className={cn(DEFAULT_TD, 'text-right')}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  errorField="ncPhdPrice"
                                  value={items.ncPhdPrice}
                                  maxLength={5}
                                  allowDecimal={true}
                                  suffix="만원"
                                  onChange={(val) =>
                                    updateKidsItem(index, 'ncPhdPrice', val)
                                  }
                                />
                              </td>
                              <td
                                className={cn(DEFAULT_TD, 'text-right')}
                                onClick={handleTdClick}
                              >
                                <PriceInput
                                  errorField="cdPhdPrice"
                                  value={items.cdPhdPrice}
                                  maxLength={5}
                                  allowDecimal={true}
                                  suffix="만원"
                                  onChange={(val) =>
                                    updateKidsItem(index, 'cdPhdPrice', val)
                                  }
                                />
                              </td>
                              <td
                                className={cn(DEFAULT_TD)}
                                rowSpan={2}
                                onPointerDown={(e) => e.stopPropagation()}
                              >
                                <div
                                  className="flex cursor-pointer items-center justify-center"
                                  onClick={(e) => {
                                    const deleteExecute = () => {
                                      setForm((prev) => ({
                                        ...prev,
                                        kids: (prev?.kids || []).filter(
                                          ({ deviceId, uuid }) =>
                                            `${items.deviceId}/${items.uuid}` !==
                                            `${deviceId}/${uuid}`,
                                        ),
                                      }))
                                    }
                                    deletePopoverClick(e, deleteExecute)
                                  }}
                                >
                                  <img
                                    src="/static/icons/icon_delete.svg"
                                    className="h-[14px] w-[14px] shrink-0"
                                    alt="icon_delete"
                                  />
                                </div>
                              </td>
                            </tr>
                            {form.isKidsFedActive && (
                              <tr>
                                <td className={cn(DEFAULT_TH, 'text-center')}>
                                  선약
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] text-right',
                                  )}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.fedFee}
                                    errorField="fedFee"
                                    maxLength={7}
                                    allowDecimal={false}
                                    onChange={(val) =>
                                      updateKidsItem(
                                        index,
                                        'fedFee',
                                        val as number,
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] px-0 py-0',
                                  )}
                                >
                                  <PeriodSelect
                                    value={items.fedPeriodType as PERIOD_TYPE}
                                    errorField="fedPeriodType"
                                    onChange={(val) =>
                                      updateKidsItem(
                                        index,
                                        'fedPeriodType',
                                        val,
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] text-right',
                                  )}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.fedUsePeriod}
                                    errorField="fedUsePeriod"
                                    maxLength={
                                      items.fedPeriodType !== 'DAY' ? 2 : 3
                                    }
                                    allowDecimal={false}
                                    periodType={
                                      items.fedPeriodType as PERIOD_TYPE
                                    }
                                    onChange={(val) =>
                                      updateKidsItem(index, 'fedUsePeriod', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'text-right')}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    errorField="naFedPrice"
                                    value={items.naFedPrice}
                                    maxLength={5}
                                    allowDecimal={true}
                                    suffix="만원"
                                    onChange={(val) =>
                                      updateKidsItem(index, 'naFedPrice', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'text-right')}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    errorField="ncFedPrice"
                                    value={items.ncFedPrice}
                                    maxLength={5}
                                    allowDecimal={true}
                                    suffix="만원"
                                    onChange={(val) =>
                                      updateKidsItem(index, 'ncFedPrice', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'text-right')}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    errorField="cdFedPrice"
                                    value={items.cdFedPrice}
                                    maxLength={5}
                                    allowDecimal={true}
                                    suffix="만원"
                                    onChange={(val) =>
                                      updateKidsItem(index, 'cdFedPrice', val)
                                    }
                                  />
                                </td>
                              </tr>
                            )}
                          </DraggableRow>
                        ))}
                    </table>
                  </SortableContext>
                </DndContext>
              </CoverWrapper>
              <Button
                variant="primary"
                className="float-right mt-[10px]"
                onClick={() => {
                  openModal(MODAL_KEY.DeviceDefaultSelect, {
                    title: '키즈폰',
                    used: form.kids as newKidsQuotationObject[],
                    category: 'KIDS',
                    source: isPartner ? 'partner' : 'store',
                    onSelected: (_selected: any[]) => {
                      let _item = [] as any
                      _selected.map((item) =>
                        _item.push({
                          uuid: uuidv4(),
                          deviceId: item?.deviceId || '',
                          deviceName: item?.deviceName || '',
                          storage: item?.storage || '',
                          isMonth: true,
                        }),
                      )

                      setForm((prev) => ({
                        ...prev,
                        kids: [...(prev.kids || []), ..._item],
                      }))
                      SuccessSnackbar()
                    },
                  })
                }}
                label="항목 추가"
              />
            </section>

            <section key={'additional-section'} id={`additional-section`}>
              <CoverWrapper
                title="부가서비스"
                description={`*모든 행은 추가, 삭제 가능해요.
  *불필요한 부가서비스(부가서비스가 없는 경우)는 꼭 삭제해주세요! 공란이 있을 경우 제출이 되지 않습니다.
  *무료는 0, 받는 비용은 ‘일반 숫자'를 기입해주세요.
  *부가서비스 금액이 범위로 표기되어야 한다면, 최소값을 입력하시고 ‘기기에 따라 요금 상이'버튼을 On시켜주세요. 
  (ex. ‘기기에 따라 요금 상이'를 On시면 ‘파손보험 8,800~’ 이렇게 최소값 기준으로 표기됩니다.)`}
              >
                <table className="w-full table-fixed border-collapse">
                  <colgroup>
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '144px' }} />
                    <col style={{ width: '100px' }} />
                    <col />
                    <col />
                    <col />
                    <col style={{ width: '83px' }} />
                    <col />
                    <col style={{ width: '48px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {ADDITIONAL_HEADER.map(({ label, require }, index) => (
                        <th key={index} className={cn(DEFAULT_TH, 'h-[56px]')}>
                          <div className="flex-row justify-center whitespace-pre-line">
                            {label}
                            {require && <span className="text-[20px]">*</span>}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isSuccess &&
                      (form?.additionalServices || []).map((items, index) => {
                        return (
                          <tr key={index} id={`additional-${index}`}>
                            <td className={cn(DEFAULT_TD, '')}>
                              {
                                TELECOM_TYPE?.[
                                items.telecom as keyof typeof TELECOM_TYPE
                                ]
                              }
                            </td>
                            <td className={cn(DEFAULT_TD, '')}>
                              {items.serviceName}
                            </td>
                            <td className={cn(DEFAULT_TD, 'px-0 py-0')}>
                              <PeriodSelect
                                className="h-full"
                                value={items.periodType as PERIOD_TYPE}
                                onChange={(periodType) =>
                                  updateAdditionalItem(
                                    index,
                                    'periodType',
                                    periodType,
                                  )
                                }
                              />
                            </td>
                            <td
                              className={cn(
                                DEFAULT_TD,
                                'text-right text-[11px]',
                              )}
                              onClick={handleTdClick}
                            >
                              <PriceInput
                                value={items.usePeriod}
                                errorField="additionalUsePeriod"
                                allowDecimal={false}
                                periodType={items.periodType as PERIOD_TYPE}
                                maxLength={items.periodType !== 'DAY' ? 2 : 3}
                                onChange={(val) =>
                                  updateAdditionalItem(index, 'usePeriod', val)
                                }
                              />
                            </td>
                            <td className={cn(DEFAULT_TD, 'py-0')}>
                              <div className="flex justify-center">
                                <Switch
                                  checked={items.isVariable}
                                  value={items.isVariable}
                                  onChange={() =>
                                    updateAdditionalItem(
                                      index,
                                      'isVariable',
                                      !items.isVariable,
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td
                              className={cn(
                                COLOR_TD,
                                'bg-[#FFFCE9] text-right',
                              )}
                              onClick={handleTdClick}
                            >
                              <PriceInput
                                value={items.serviceFee}
                                errorField="additionalServiceFee"
                                maxLength={7}
                                allowDecimal={false}
                                suffix="원"
                                onChange={(val) =>
                                  updateAdditionalItem(
                                    index,
                                    'serviceFee',
                                    val as number,
                                  )
                                }
                              />
                            </td>
                            <td className={cn(DEFAULT_TD, 'py-0')}>
                              <div className="flex flex-row items-center justify-center">
                                <span className="whitespace-nowrap">필수</span>
                                <Checkbox
                                  checked={items.isMandatory}
                                  onClick={() =>
                                    updateAdditionalItem(
                                      index,
                                      'isMandatory',
                                      !items.isMandatory,
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td
                              className={cn(
                                COLOR_TD,
                                items.isMandatory
                                  ? 'bg-[#7676801F]/12'
                                  : 'bg-white',
                                'text-right',
                                items.isMandatory && 'text-[#C9C5C6]',
                              )}
                              onClick={handleTdClick}
                            >
                              <PriceInput
                                errorField="nonSubscriptionFee"
                                value={
                                  items.isMandatory
                                    ? '-'
                                    : items.nonSubscriptionFee
                                }
                                maxLength={5}
                                allowDecimal={true}
                                suffix="만원"
                                disabled={items.isMandatory}
                                onChange={(val) =>
                                  updateAdditionalItem(
                                    index,
                                    'nonSubscriptionFee',
                                    val,
                                  )
                                }
                              />
                            </td>
                            <td className={cn(DEFAULT_TD)}>
                              <div
                                className="flex cursor-pointer items-center justify-center"
                                onClick={(e) => {
                                  const deleteExecute = () => {
                                    setForm((prev) => ({
                                      ...prev,
                                      additionalServices: (
                                        prev?.additionalServices || []
                                      ).filter(
                                        (_, addIndex) => index !== addIndex,
                                      ),
                                    }))
                                  }
                                  deletePopoverClick(e, deleteExecute)
                                }}
                              >
                                <img
                                  src="/static/icons/icon_delete.svg"
                                  className="h-[14px] w-[14px] shrink-0"
                                  alt="icon_delete"
                                />
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </CoverWrapper>
              <Button
                variant="primary"
                className="float-right mt-[10px]"
                onClick={() => {
                  openModal(MODAL_KEY.AdditionalCreate, {
                    onSelected: (_selected: additionalQuotationObject[]) => {
                      setForm((prev) => ({
                        ...prev,
                        additionalServices: [
                          ...(prev?.additionalServices || []),
                          ..._selected,
                        ],
                      }))
                      SuccessSnackbar()
                    },
                  })
                }}
                label="항목 추가"
              />
            </section>

            {!isSectionHidden('event') && (
            <section key={'event-section'} id={`event-section`}>
              <CoverWrapper
                title="이벤트"
                description={`*증정 등의 이벤트를 자유롭게 작성해주세요. (ex. 갤럭시 워치 무료 지급 이벤트 - 선착순 10명) `}
              >
                <table className="w-full table-auto border-collapse">
                  <thead>
                    <tr>
                      <th className={cn(DEFAULT_TH, 'h-[56px]')}>
                        이벤트 내용
                      </th>
                      <th className={cn(DEFAULT_TH, 'w-[48px]')}>삭제</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form?.event && (
                      <tr>
                        <td className={cn(DEFAULT_TD)}>{form?.event}</td>
                        <td
                          className={cn(DEFAULT_TD)}
                          onClick={(e) => {
                            const deleteExecute = () => {
                              setForm((prev) => ({
                                ...prev,
                                event: '',
                                etc: '',
                              }))
                            }
                            deletePopoverClick(e, deleteExecute)
                          }}
                        >
                          <img
                            src="/static/icons/icon_delete.svg"
                            className="h-[14px] w-[14px] shrink-0"
                            alt="icon_delete"
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {!form?.event && (
                  <Button
                    variant="primary"
                    className="float-right mt-[10px]"
                    onClick={() => {
                      openModal(MODAL_KEY.EventCreate, {
                        changed: (value) => {
                          setForm((prev) => ({ ...prev, event: value }))
                          SuccessSnackbar()
                        },
                      })
                    }}
                    label="항목 추가"
                  />
                )}
              </CoverWrapper>
            </section>
            )}

            {!isSectionHidden('usim') && (
            <>
            <div className="bg-gray-20 my-[30px] h-[10px] w-full" />

            <section key={'usim-section'} id={`usim-section`}>
              <div className="border-opacity-black-20 rounded-[8px] border p-5">
                <p className="text-style-compact-04 text-primary-50 py-2 font-normal">
                  *위 필수요금제, 부가서비스 등과 관련없는 단독 상품
                </p>
                <CoverWrapper
                  title="유심이동 상품"
                  description={`**모든 행은 추가, 삭제, 순서변경 가능해요.
  *'만원' 단위의 양수(마이너스x)로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다.
  *통신사별 하나의 유심이동 상품만 입력가능합니다. (가족결합 가입의 경우 제외하고 1인 가입시 금액을 입력해주세요.)
  *유심이동 상품은 위의 통신사별 필수 요금제 및 부가서비스 영향을 받지 않는 단독 상품입니다. (부가서비스 가입 없는 조건으로 입력해주세요)
  *유심이동은 선택약정 상품이며 ‘12개월(1년) 약정’의 기준 가격으로 입력해주세요.`}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={({ active, over }) => {
                      if (!over || active.id === over.id) return
                      const oldIndex = (form?.usims || []).findIndex(
                        (items) => `${items.telecom}` == active.id,
                      )
                      const newIndex = (form?.usims || []).findIndex(
                        (items) => `${items.telecom}` == over.id,
                      )
                      let _usims = arrayMove(
                        form?.usims || [],
                        oldIndex,
                        newIndex,
                      ) as usimQuotationObject[]
                      setForm((state) => ({ ...state, usims: _usims }))
                    }}
                  >
                    <SortableContext
                      items={(form?.usims ?? []).map(
                        (items) => `${items.telecom}`,
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      <table className="w-full table-auto border-collapse">
                        <colgroup>
                          <col style={{ width: '40px' }} />
                          <col style={{ width: '80px' }} />
                          <col />
                          <col style={{ width: '100px' }} />
                          <col style={{ minWidth: '160px' }} />
                          <col />
                          <col style={{ width: '40px' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            {USIM_HEADER.map(
                              ({ label, colspan, require }, index) => (
                                <th
                                  key={index}
                                  className={cn(DEFAULT_TH, 'h-[56px]')}
                                  colSpan={colspan}
                                >
                                  <div className="flex-row justify-center">
                                    {label}
                                    {require && (
                                      <span className="text-[20px]">*</span>
                                    )}
                                  </div>
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>

                        {isSuccess &&
                          (form?.usims || []).map((items, index) => (
                            <DraggableRow
                              key={`${items.telecom}`}
                              id={`${items.telecom}`}
                            >
                              <tr>
                                <td className={cn(DEFAULT_TD)}>
                                  <div className="flex items-center justify-center">
                                    <img
                                      src="/static/icons/icon_recoder.svg"
                                      className="h-[14px] w-[14px] shrink-0"
                                      alt="recorder icon"
                                    />
                                  </div>
                                </td>
                                <td className={cn(DEFAULT_TD)}>
                                  {
                                    TELECOM_TYPE[
                                    items.telecom as keyof typeof TELECOM_TYPE
                                    ]
                                  }
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] text-right text-[11px]',
                                  )}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.fee}
                                    errorField="usimFee"
                                    maxLength={7}
                                    placeholder="할인전 요금제 금액 입력"
                                    allowDecimal={true}
                                    suffix="원"
                                    onChange={(val) =>
                                      updateUsimItem(index, 'fee', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] px-0 py-0',
                                  )}
                                >
                                  <PeriodSelect
                                    value={items.periodType as PERIOD_TYPE}
                                    errorField="usimPeriodType"
                                    onChange={(periodType) =>
                                      updateUsimItem(
                                        index,
                                        'periodType',
                                        periodType,
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(
                                    COLOR_TD,
                                    'bg-[#FFFCE9] text-right text-[11px]',
                                  )}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.usePeriod}
                                    errorField="usimUsePeriod"
                                    periodType={items.periodType as PERIOD_TYPE}
                                    maxLength={
                                      items.periodType !== 'DAY' ? 2 : 3
                                    }
                                    allowDecimal={false}
                                    onChange={(val) =>
                                      updateUsimItem(index, 'usePeriod', val)
                                    }
                                  />
                                </td>
                                <td
                                  className={cn(DEFAULT_TD, 'text-right')}
                                  onClick={handleTdClick}
                                >
                                  <PriceInput
                                    value={items.price}
                                    errorField="usimPrice"
                                    maxLength={5}
                                    allowDecimal={true}
                                    regex={/^\d*\.?\d*$/}
                                    suffix="만원"
                                    onChange={(val) =>
                                      updateUsimItem(index, 'price', val)
                                    }
                                  />
                                </td>
                                <td className={cn(DEFAULT_TD)}
                                  onPointerDown={(e) => {
                                    e.stopPropagation()
                                  }}
                                >
                                  <div
                                    className="flex cursor-pointer items-center justify-center"
                                    onClick={(e) => {
                                      const deleteExecute = () => {
                                        setForm((prev) => ({
                                          ...prev,
                                          usims: (prev?.usims || []).filter(
                                            ({ telecom }) =>
                                              `${items.telecom}` !==
                                              `${telecom}`,
                                          ),
                                        }))
                                      }
                                      deletePopoverClick(e, deleteExecute)
                                    }}
                                  >
                                    <img
                                      src="/static/icons/icon_delete.svg"
                                      className="h-[14px] w-[14px] shrink-0"
                                      alt="icon_delete"
                                    />
                                  </div>
                                </td>
                              </tr>
                            </DraggableRow>
                          ))}
                      </table>
                    </SortableContext>
                  </DndContext>
                </CoverWrapper>
                <div className="w-full pt-[10px] text-right">
                  <Button
                    variant="primary"
                    onClick={() => {
                      openModal(MODAL_KEY.UsimTelecomSelect, {
                        used: form.usims,
                        onClick: (data) => {
                          let _item = [] as any
                          data.forEach((item) => {
                            if (item.type === 'ADD') {
                              _item.push({
                                type: 'ADD',
                                telecom: item.telecom,
                                fee: null,
                                price: null,
                                sortOrder: null,
                                usePeriod: null,
                                isMonth: true,
                              })
                            }
                          })
                          setForm((prev) => ({
                            ...prev,
                            usims: [...(prev?.usims || []), ..._item],
                          }))
                          SuccessSnackbar()
                        },
                      })
                    }}
                    label="항목 추가"
                  />
                </div>
              </div>
            </section>
            </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}
